import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import PublicOffersService, { type PublicOffer, readPublicOfferFields } from '~/services/PublicOfferService';

import { buildNameLookupMap } from '../buildNameLookupMap';
import { useNormalizedIds } from '../useNormalizedIds';

import { publicOfferQueryKeys } from './publicOfferQueryKeys';

export function useGetPublicOffersByIds(ids: Array<string>, region: string, brand: App.Tenant['brand']) {
  const normalizedIds = useNormalizedIds(ids);
  const isEnabled = Boolean(region && brand && normalizedIds.length > 0);

  const { data, isLoading, isError } = useQuery({
    queryKey: publicOfferQueryKeys.byIds(normalizedIds, region, brand),
    queryFn: async ({ signal }) => {
      return await PublicOffersService.getPublicOffersByIds(normalizedIds, region, brand, { signal });
    },
    enabled: isEnabled,
    retry: false,
  });

  const offersById = useMemo(() => data ?? new Map<string, PublicOffer>(), [data]);

  const offerNamesById = useMemo(() => {
    return buildNameLookupMap(
      normalizedIds,
      (id) => ({ name: readPublicOfferFields(offersById.get(id)).offerName ?? null }),
      {
        isLoading,
        isError,
      },
    );
  }, [normalizedIds, offersById, isLoading, isError]);

  const hotelDetailsById = useMemo(() => {
    return buildNameLookupMap(
      normalizedIds,
      (id) => {
        const fields = readPublicOfferFields(offersById.get(id));

        return { name: fields.hotelName ?? fields.offerName ?? null, slug: fields.offerSlug ?? null };
      },
      {
        isLoading,
        isError,
      },
    );
  }, [normalizedIds, offersById, isLoading, isError]);

  return { hotelDetailsById, offerNamesById, offersById, isLoading, isError };
}
